import {
  SOURCE_ADMIN_SMS,
  SOURCE_TCAPI,
  type LocationSources,
} from '@rsos/constants/locationSources';
import { ConversationStatus } from '@rsos/hermes-js/src/typings/conversation';

type conversationSliceAPIChecks =
  | 'createConversation'
  | 'endConversation'
  | 'getConversationMetadata';

export type ConversationErrorRecord = Record<
  conversationSliceAPIChecks,
  unknown
>;
export type ConversationLoadingRecord = Record<
  conversationSliceAPIChecks,
  boolean
>;

// Only 'pending' is currently sent by the backend. Other values are speculative.
export type TransferStatus = 'pending' | 'accepted' | 'timed_out';

export interface ConversationTransfer {
  status: TransferStatus;
  /** Display name of the authority the call is being transferred to (e.g. "Cabell County ECC"). */
  receivingAuthorityDisplayName: string | null;
  /** ID of the authority the call is being transferred to (e.g. "Cabell County ECC"). */
  receivingAuthorityAccountId: string | null;
  /** Display name of the authority the call is being transferred from (e.g. "Cabell County ECC"). */
  initiatingAuthorityDisplayName: string | null;
  /** ID of the authority the call is being transferred from (e.g. "Cabell County ECC"). */
  initiatingAuthorityAccountId: string | null;
}

interface baseConversation {
  error: ConversationErrorRecord;
  loading: ConversationLoadingRecord;
  source: LocationSources;
  data: unknown;
  isUnread: boolean;
  recentMessageType: ConversationStatus;
  /** Present when a TCAPI soft transfer (conference) is in progress. Set when a conversation.transfer message arrives via the communicator exchange. */
  transfer?: ConversationTransfer;
}

interface errorConversation extends baseConversation {
  data: null;
}

export interface TCAPIConversationData {
  serverTimeMillis: number;
  conversationId: string;
  authorityAccountId: string;
  authorityDisplayName: string;
  twilioPhone: string | null;
  callerId: string;
  caller_id: string;
  creationTime: number;
  lastUpdatedTime: number;
  translationMethod: string | null;
  lastCallerLanguage: string | null;
  status: 'pending' | 'active' | 'inactive';
  sessionType: 'sms' | 'rcs' | 'sms_admin' | 'tcapi';
  acceptStatus: 'pending' | 'accepted' | 'timed_out' | 'declined';
  expireTime: number | null;
  lastAcceptTime: number | null;
  sessionId: string;
}

interface conversationTCAPIBase extends baseConversation {
  source: typeof SOURCE_TCAPI;
}

/** Conversation metadata has been fetched from the TCAPI session endpoint. */
interface conversationTCAPILoaded extends conversationTCAPIBase {
  data: TCAPIConversationData;
}

/** Metadata fetch is in-flight or hasn't started yet. data is null because the matchPending extraReducer clears it when a getTCAPIConversationMetadata request begins. */
interface conversationTCAPIPending extends conversationTCAPIBase {
  data: null;
}

export type conversationTCAPI =
  | conversationTCAPILoaded
  | conversationTCAPIPending;

/**
 * Type guard: narrows to a TCAPI conversation (loaded or pending).
 */
export function isTCAPIConversation(
  conversation: conversationStateValues,
): conversation is conversationTCAPI {
  return conversation.source === SOURCE_TCAPI;
}

/**
 * Type guard: narrows to a conversation with loaded TCAPI metadata. Note that conversations from other sources will not pass this check, even if they have a data field with similar properties to TCAPI conversations.
 */
export function isTCAPIConversationLoaded(
  conversation: conversationStateValues,
): conversation is conversationTCAPILoaded {
  return isTCAPIConversation(conversation) && conversation.data !== null;
}

interface conversationADMINSMS extends baseConversation {
  source: typeof SOURCE_ADMIN_SMS;
  data: {
    extra_info: unknown;
    created_time: number;
    source: 'admin_sms';
    application: 'text_to_911';
    caller_id: string;
    conversation_start_time: number;
    valid_for_psap: boolean;
    conversation_id: string;
  };
}

export type conversationStateValues =
  | conversationADMINSMS
  | conversationTCAPI
  | errorConversation;
